'use client';

import React, { useState } from 'react';
import SearchBar from './SearchBar';
import SectionAccordion from './SectionAccordion';
import ContactSupport from './ContactSupport';
import Footer from '@/app/homepage/components/Footer';

interface GuideSection {
  id: string;
  title: string;
  icon: string;
  content: {
    subtitle?: string;
    items: string[];
  }[];
}

const SupportCenterInteractive: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSection, setExpandedSection] = useState<string | null>('quick-start');

  const guideSections: GuideSection[] = [
    {
      id: 'quick-start',
      title: 'Quick Start',
      icon: '🚀',
      content: [
        {
          items: [
            'Install and open WordFirst',
            'Choose: Continue as Guest or Sign In / Create Account',
            'From the Landing Screen: Tap Word of the Week, Bookmarks, Notes, or Search. Open the Bible browser to pick a book and chapter',
            'Tap a verse to view more actions (e.g., add to playlist/bookmarks, play audio)',
            'For audio features beyond previews, sign in and subscribe',
          ],
        },
      ],
    },
    {
      id: 'account-authentication',
      title: 'Account and Authentication',
      icon: '🔐',
      content: [
        {
          subtitle: 'Guest Mode',
          items: [
            'Continue without an account for quick reading',
            'Guest data (bookmarks/playlists) is stored locally via Guest Data Manager; it can later migrate to your account',
            'Some features (e.g., full audio, offline downloads) require sign-in and an active subscription',
          ],
        },
        {
          subtitle: 'Sign In / Sign Up',
          items: [
            'Use email/password or Google Sign-In',
            'The Sign In and Sign Up screens handle form validation and standard auth flows with Firebase',
            'On sign-in, AuthStateManager tracks your status, and the UI updates accordingly',
          ],
        },
        {
          subtitle: 'Email Verification',
          items: [
            'The app enforces verified emails during authentication flows',
            'Once verified, you have normal access to protected features',
          ],
        },
      ],
    },
    {
      id: 'home-navigation',
      title: 'Home and Navigation',
      icon: '🏠',
      content: [
        {
          subtitle: 'Landing Screen',
          items: [
            'Overview hub with shortcuts: My Playlist, Search, Bookmarks, Notes, Word of the Week, and current subscription status',
            'Shows your current app mode and quick access to common areas',
          ],
        },
        {
          subtitle: 'Bible Browsing',
          items: [
            'Use the Bible browser to choose a book and chapter, then view verses',
            'Under the hood, Bible content is queried efficiently via DatabaseHelper',
          ],
        },
        {
          subtitle: 'My Playlists',
          items: ['Provides a shortcut to all configured playlists'],
        },
        {
          subtitle: 'Word of the Week',
          items: [
            'Fetches weekly Word of the Week content containing title, verse, and content',
            'Updates periodically with a retry interval',
          ],
        },
        {
          subtitle: 'Search',
          items: [
            'Search across verses by text',
            'Smart parsing recognizes Bible references (e.g., "John 3:16", "Gen 1") using BibleReferenceParser and jumps to the location',
          ],
        },
        {
          subtitle: 'Bookmarks',
          items: [
            'Save favorite verses for quick access',
            'Works for guests (local storage) and authenticated users',
          ],
        },
        {
          subtitle: 'Playlists',
          items: [
            'Create collections of verses for playback',
            'Manage items and order, then play or download for offline',
          ],
        },
        {
          subtitle: 'Notes',
          items: [
            'Create and manage notes with title, content, tags, favorite, and color',
            'Search notes and filter by favorites or recents',
          ],
        },
        {
          subtitle: 'Settings',
          items: ['Adjust narration voice, app mode, audio speed, and background sound'],
        },
        {
          subtitle: 'Contact Us',
          items: ['Send feedback via an in-app form or open mail/links'],
        },
      ],
    },
    {
      id: 'bible-reading',
      title: 'Bible Reading Experience',
      icon: '📖',
      content: [
        {
          subtitle: 'Books, Chapters, Verses',
          items: [
            'Book list → Chapter list → Verse list',
            'Chapter and verse screens are optimized for performance (including optional itemExtent and cached extents in OptimizedListView)',
          ],
        },
        {
          subtitle: 'Verse of the Day',
          items: ['VerseOfTheDayHelper returns a consistent verse for the current day'],
        },
        {
          subtitle: 'Optimized Lists for Performance',
          items: ['Long lists use tuned list views for fast scrolling and lower memory overhead'],
        },
      ],
    },
    {
      id: 'search-parsing',
      title: 'Search and Smart Parsing',
      icon: '🔍',
      content: [
        {
          items: [
            'Type keywords to search Bible text',
            'Enter a reference like "1 Cor 13" and the parser resolves book abbreviations and chapter/verse patterns',
            'If a reference is detected, the app navigates to that passage; otherwise it performs a normal text search',
          ],
        },
      ],
    },
    {
      id: 'audio-features',
      title: 'Audio Features',
      icon: '🎵',
      content: [
        {
          subtitle: 'Online Audio',
          items: [
            'Tap the play button near verses/chapters to stream audio using just_audio via AudioPlayerManager',
            'The manager ensures one active player per context to avoid overlapping playback',
          ],
        },
        {
          subtitle: 'Offline-Aware Player',
          items: [
            'OfflineAwareAudioPlayer checks network conditions and uses offline files when available',
            'If you\'re offline without a download, it shows helpful messages from NetworkHelper',
          ],
        },
        {
          subtitle: 'Background Sounds and Speed',
          items: [
            'In Settings, choose narration style (male/female), toggle background sounds (strings, nature, gentle piano, wood burning), and set volume',
            'Adjust playback speed globally, applied wherever audio is played',
          ],
        },
        {
          subtitle: 'Access Control (Auth + Subscription)',
          items: [
            'EnhancedAudioAccessHelper and SecureAudioAccessHelper enforce access',
            'You must be signed in and have an active entitlement',
            'If access is denied, you\'ll see a prompt to subscribe or sign in',
            'Signed URLs for audio are cached with expiration via SecureAudioUrlCache',
          ],
        },
      ],
    },
    {
      id: 'bookmarks',
      title: 'Bookmarks',
      icon: '🔖',
      content: [
        {
          items: [
            'From verse options, choose Add to Bookmarks',
            'Manage your bookmarks in the Bookmarks screen',
            'Authenticated users\' bookmarks are associated with your account; guests\' bookmarks are stored locally',
            'You can remove bookmarks individually or by verse reference',
          ],
        },
      ],
    },
    {
      id: 'playlists',
      title: 'Playlists',
      icon: '📝',
      content: [
        {
          subtitle: 'Creating, Managing, Playing',
          items: [
            'Add verses to a playlist from the verse options menu',
            'Create new playlists, rename, and reorder items',
            'Play a playlist; the player handles sequential playback and honors your global audio settings',
          ],
        },
        {
          subtitle: 'Offline Downloads',
          items: [
            'Download playlist audio for offline use from the Playlist screen',
            'Download status: not downloaded, downloading, downloaded',
            'Progress and counts are shown (e.g., via OfflineStatusWidget)',
            'Downloads save to app storage; network errors show recovery guidance',
          ],
        },
      ],
    },
    {
      id: 'notes',
      title: 'Notes',
      icon: '📓',
      content: [
        {
          subtitle: 'Create, Edit, Tag, Favorite',
          items: [
            'Create rich notes with title and content',
            'Add tags as comma-separated values',
            'Mark important notes as favorites',
            'Choose a note color for organization',
          ],
        },
        {
          subtitle: 'Search and Filters',
          items: [
            'Search by title/content/tags',
            'Filter: All, Favorites, Recent',
            'Multi-select actions for bulk operations are available',
          ],
        },
      ],
    },
    {
      id: 'subscription',
      title: 'Subscription and Entitlements',
      icon: '💳',
      content: [
        {
          subtitle: 'Subscription Status Bar',
          items: [
            'A compact, dismissible banner on the Landing Screen shows your current status',
            'It refreshes periodically and when the app resumes',
          ],
        },
        {
          subtitle: 'Purchase Flow',
          items: [
            'Purchases are handled securely with server-side validation',
            'Product IDs are centralized in ProductConstants to match the store setup',
            'After purchasing, entitlements are updated and audio access is granted',
          ],
        },
        {
          subtitle: 'Cancellation',
          items: [
            'If a subscription is canceled, EnhancedSubscriptionManager and SubscriptionCancellationManager guide you through next steps',
            'Cache invalidation and cleanups are performed automatically',
          ],
        },
        {
          subtitle: 'Device Limits',
          items: [
            'To prevent account sharing, device registration is enforced',
            'You may only register a limited number of devices per account',
            'If the limit is exceeded, you\'ll see the Device Limit Exceeded dialog with management options',
          ],
        },
      ],
    },
    {
      id: 'device-management',
      title: 'Device Management',
      icon: '📱',
      content: [
        {
          items: [
            'Open Device Management from Settings or prompts',
            'View registered devices: name, platform, and last active time',
            'Remove old devices to free a slot for your current device',
            'Device IDs are generated using secure device info; changes may require re-registration',
          ],
        },
      ],
    },
    {
      id: 'offline-network',
      title: 'Offline & Network Behavior',
      icon: '🌐',
      content: [
        {
          items: [
            'The app detects connectivity via Connectivity and handles common network errors gracefully',
            'Messages help you understand whether to retry, switch networks, or use offline content',
            'Playlist downloads verify connectivity, support pause/resume, and clean up partial files on failure',
          ],
        },
      ],
    },
    {
      id: 'global-settings',
      title: 'Global Settings',
      icon: '⚙️',
      content: [
        {
          items: [
            'Narration Style: Male or Female',
            'App Mode: Light or Dark',
            'Audio Speed: 0.9x–1.1x',
            'Background Sound: Strings, Nature Sounds, Gentle Piano, Wood Burning, with volume control',
            'Word of the Week: Enable/disable display',
          ],
        },
      ],
    },
    {
      id: 'security',
      title: 'Security and Integrity',
      icon: '🔒',
      content: [
        {
          items: [
            'Firebase App Check is enabled to protect against tampering, with platform-appropriate providers',
            'Audio URLs are signed and cached only until expiry; refresh logic prevents stale links',
            'Subscription entitlements are verified server-side via Cloud Functions and Firestore',
            'Device registration ties entitlements to your devices within allowed limits',
          ],
        },
      ],
    },
    {
      id: 'troubleshooting',
      title: 'Troubleshooting',
      icon: '🔧',
      content: [
        {
          subtitle: 'I can\'t play audio',
          items: [
            'Ensure you\'re signed in and have an active subscription',
            'Check network connection or play downloaded/offline items',
            'If you hit a device limit, remove an old device',
          ],
        },
        {
          subtitle: 'Downloads fail or stop',
          items: [
            'Confirm network stability and free storage space',
            'Retry from the Playlist screen; partial files are handled safely',
          ],
        },
        {
          subtitle: 'Search doesn\'t find my verse',
          items: [
            'Try exact phrases or use a reference like "Psalm 23:1"',
            'Abbreviations like "Ps 23" are supported',
          ],
        },
        {
          subtitle: 'Notes or bookmarks missing after sign-in',
          items: [
            'Guest data migrates after you authenticate',
            'Give it a moment or restart the app',
          ],
        },
      ],
    },
    {
      id: 'faq',
      title: 'FAQ',
      icon: '❓',
      content: [
        {
          subtitle: 'Do I need an account to use the app?',
          items: [
            'No. You can read as a guest. Audio, offline downloads, and syncing require sign-in and an active subscription',
          ],
        },
        {
          subtitle: 'How many devices can I use?',
          items: [
            'There is a limit to prevent sharing. If you reach it, manage devices from the Device Management screen',
          ],
        },
        {
          subtitle: 'Can I use the app offline?',
          items: [
            'Yes. Download playlists for offline audio and access previously viewed content',
            'Search and some features need a connection',
          ],
        },
      ],
    },
  ];

  const filteredSections = guideSections.filter((section) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      section.title.toLowerCase().includes(query) ||
      section.content.some((contentBlock) =>
        contentBlock.items.some((item) => item.toLowerCase().includes(query))
      )
    );
  });

  const toggleSection = (sectionId: string) => {
    setExpandedSection(expandedSection === sectionId ? null : sectionId);
  };

  return (
    <main data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:451:4" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="451" data-component-file="SupportCenterInteractive.tsx" data-component-name="main" data-component-content="%7B%22elementName%22%3A%22main%22%2C%22className%22%3A%22min-h-screen%20bg-background%20pt-16%22%7D" className="min-h-screen bg-background pt-16">
      {/* Hero Section */}
      <section data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:453:6" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="453" data-component-file="SupportCenterInteractive.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22className%22%3A%22bg-gradient-to-b%20from-primary%2F5%20to-background%20py-12%20md%3Apy-16%22%7D" className="bg-gradient-to-b from-primary/5 to-background py-12 md:py-16">
        <div data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:454:8" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="454" data-component-file="SupportCenterInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-4xl%20mx-auto%20px-6%20lg%3Apx-8%20text-center%22%7D" className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h1 data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:455:10" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="455" data-component-file="SupportCenterInteractive.tsx" data-component-name="h1" data-component-content="%7B%22elementName%22%3A%22h1%22%2C%22className%22%3A%22font-headline%20font-bold%20text-4xl%20md%3Atext-5xl%20text-textPrimary%20mb-4%22%2C%22textContent%22%3A%22WordFirst%20User%20Guide%22%7D" className="font-headline font-bold text-4xl md:text-5xl text-textPrimary mb-4">
            WordFirst User Guide
          </h1>
          <p data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:458:10" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="458" data-component-file="SupportCenterInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22font-body%20text-lg%20md%3Atext-xl%20text-textSecondary%20mb-8%22%2C%22textContent%22%3A%22Everything%20you%20need%20to%20know%20about%20using%20WordFirst%20on%20iOS%20and%20Android%22%7D" className="font-body text-lg md:text-xl text-textSecondary mb-8">
            Everything you need to know about using WordFirst on iOS and Android
          </p>
          <SearchBar data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:461:10" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="461" data-component-file="SupportCenterInteractive.tsx" data-component-name="SearchBar" data-component-content="%7B%22elementName%22%3A%22SearchBar%22%2C%22value%22%3A%22%5Bvar%3AsearchQuery%5D%22%7D" value={searchQuery} onChange={setSearchQuery} />
        </div>
      </section>

      {/* Guide Sections */}
      <section data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:466:6" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="466" data-component-file="SupportCenterInteractive.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22className%22%3A%22max-w-4xl%20mx-auto%20px-6%20lg%3Apx-8%20py-12%22%7D" className="max-w-4xl mx-auto px-6 lg:px-8 py-12">
        <div data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:467:8" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="467" data-component-file="SupportCenterInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-4%22%7D" className="space-y-4">
          {filteredSections.map((section) => (
            <SectionAccordion data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:469:12" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="469" data-component-file="SupportCenterInteractive.tsx" data-component-name="SectionAccordion" data-component-content="%7B%22elementName%22%3A%22SectionAccordion%22%7D"
              key={section.id}
              section={section}
              isExpanded={expandedSection === section.id}
              onToggle={() => toggleSection(section.id)}
            />
          ))}
        </div>

        {filteredSections.length === 0 && (
          <div data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:479:10" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="479" data-component-file="SupportCenterInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20py-12%22%7D" className="text-center py-12">
            <p data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:480:12" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="480" data-component-file="SupportCenterInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22font-body%20text-lg%20text-textSecondary%22%2C%22textContent%22%3A%22No%20results%20found%20for%20%5C%22%20%5C%22.%20Try%20different%20keywords.%22%7D" className="font-body text-lg text-textSecondary">
              No results found for "{searchQuery}". Try different keywords.
            </p>
          </div>
        )}
      </section>

      {/* Contact Support Section */}
      <ContactSupport data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:488:6" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="488" data-component-file="SupportCenterInteractive.tsx" data-component-name="ContactSupport" data-component-content="%7B%22elementName%22%3A%22ContactSupport%22%7D" />

      {/* Footer */}
      <Footer data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:491:6" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="491" data-component-file="SupportCenterInteractive.tsx" data-component-name="Footer" data-component-content="%7B%22elementName%22%3A%22Footer%22%7D" />
    </main>
  );
};

export default SupportCenterInteractive;